import { faCheck, faSpinner, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { useContext, useEffect, useMemo, useReducer } from 'react'
import VideoSection from '../../../components/Layout/VideoSection'
import {
  INITIALIZE,
  LIVE_CONFIRMATION,
  LOADING,
  RESEND_CONFIRMATION,
  TITLE
} from '../../../constants/actions/modSettingsActions'
import * as FEATURES from '../../../constants/features'
import * as SETTINGS from '../../../constants/settings'
import { SiteConfigContext } from '../../../context/siteConfigContext'
import {
  initialState,
  modSettingsReducer
} from '../../../reducers/modSettingsReducer'
import { putGoLive } from '../../../utils/APIs/settings'
import { toastSuccess } from '../../../utils/toast'
import { delay, setPageTitle } from '../../../utils/utilities'
import ToggleSwitch from '../../Forms/common/ToggleSwitch'
import Tooltip from '../../Utilities/Tooltip'
import ActiveStreamer from './ActiveStreamer'
import LimitedAccess from './LimitedAccess'
import Offline from './Offline'
import PreStreamVideo from './PreStreamVideo'

const TITLE_MAX_LENGTH = 140

const SiteSettings = () => {
  setPageTitle('Site Settings')
  const {
    [SETTINGS.FEATURES.key]: enabledFeatures,
    [SETTINGS.LIVESTREAM_LIVE.key]: livestreamLive,
    [SETTINGS.CHAT_OPEN.key]: chatOpen,
    [SETTINGS.STREAM_TITLE.key]: streamTitle
  } = useContext(SiteConfigContext)
  const [state, dispatch] = useReducer(modSettingsReducer, initialState)

  const preStreamEnabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.PRESTREAM],
    [enabledFeatures]
  )

  const limitedAccessEnabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.LIMITED_ACCESS],
    [enabledFeatures]
  )

  useEffect(() => {
    dispatch({
      type: INITIALIZE,
      payload: {
        live: livestreamLive,
        open: chatOpen,
        title: streamTitle
      }
    })
  }, [livestreamLive, chatOpen, streamTitle])

  useEffect(() => {
    if (streamTitle) {
      dispatch({ type: TITLE, payload: streamTitle })
    }
  }, [streamTitle])

  return (
    <div className="flex w-full gap-5 pb-5">
      <div className="flex flex-col w-full gap-5 lg:w-1/2 xl:w-2/3">
        {preStreamEnabled && (
          <div className="flex flex-col gap-5 p-5 w-full xl:bg-neutral-200/50 xl:dark:bg-neutral-800/50">
            <PreStreamVideo display="flex" />
          </div>
        )}
        <div className="flex flex-col gap-5 p-5 w-full xl:bg-neutral-200/50 xl:dark:bg-neutral-800/50">
          <div className="flex items-center">
            <h2 className="mr-3">Livestream Settings</h2>
            <Tooltip>
              This section displays your general livestream settings, including
              the incoming video feed, active streamer, and stream title.
            </Tooltip>
          </div>
          <Settings state={state} dispatch={dispatch} />
          <h2>Incoming Feed</h2>
          <VideoSection modView />
        </div>
        {limitedAccessEnabled && (
          <div className="flex flex-col gap-5 p-5 w-full xl:bg-neutral-200/50 xl:dark:bg-neutral-800/50">
            <LimitedAccess display="flex" />
          </div>
        )}
        <div className="flex flex-col gap-5 p-5 w-full xl:bg-neutral-200/50 xl:dark:bg-neutral-800/50">
          <Offline display="flex" />
        </div>
      </div>
      <div className="h-fit lg:w-1/2 xl:w-1/3 xl:p-5 flex flex-col gap-5 xl:bg-neutral-200/50 xl:dark:bg-neutral-800/50">
        <ActiveStreamer display="flex" />
      </div>
    </div>
  )
}

const Settings = ({ state, dispatch }) => {
  const { updateSetting } = useContext(SiteConfigContext)

  const handleTitleChange = (value) => {
    dispatch({ type: TITLE, payload: value })
  }

  const handleLiveConfirmationChange = (value) => {
    dispatch({ type: LIVE_CONFIRMATION, payload: value })
  }

  const handleResendConfirmationChange = (value) => {
    dispatch({ type: RESEND_CONFIRMATION, payload: value })
  }

  const handleTitle = async (e) => {
    e.preventDefault()
    await updateSetting(SETTINGS.STREAM_TITLE, state.title)
    toastSuccess('Stream Title updated.')
  }

  const handleGoLive = async (status) => {
    dispatch({ type: LOADING, payload: true })
    dispatch({ type: LIVE_CONFIRMATION, payload: false })
    dispatch({ type: RESEND_CONFIRMATION, payload: false })
    const response = await putGoLive(status, state.title)
    if (response && response.status === 200) {
      await delay(5000) // delay so they can't spam it
      dispatch({ type: LOADING, payload: false })
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 w-fit">
        <ToggleSwitch
          setting={SETTINGS.LIVESTREAM_LIVE}
          value={state.live}
          label="Livestream Live"
          color="green"
        />
        <ToggleSwitch
          setting={SETTINGS.CHAT_OPEN}
          value={state.open}
          label="Chat Open"
          color="green"
        />
      </div>
      <h4>
        Stream Title
        <span className="ml-2 text-xs">
          <span
            className={
              state.title.length === TITLE_MAX_LENGTH ? 'text-red-500' : ''
            }
          >
            ({state.title.length} / {TITLE_MAX_LENGTH})
          </span>
        </span>
      </h4>
      <form onSubmit={handleTitle} autoComplete="off">
        <div className="relative flex flex-grow items-stretch focus-within:z-10 gap-2">
          <input
            value={state.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="input block w-full"
            placeholder="Stream Title"
            maxLength={TITLE_MAX_LENGTH}
          />
          <button
            type="submit"
            aria-label="Submit title"
            className="button-confirm inline-flex items-center gap-x-1.5 text-base"
          >
            Set Title
          </button>
        </div>
      </form>
      <div className="flex flex-col md:flex-row gap-2">
        {!state.liveConfirmation && (
          <button
            onClick={() => handleLiveConfirmationChange(true)}
            aria-label={!state.live ? 'Go Live' : 'End Stream'}
            className={`${
              !state.live ? 'button-confirm' : 'button-cancel'
            } w-1/2 text-base`}
            disabled={state.loading}
          >
            {!state.live ? 'Go Live' : 'End Stream'}
          </button>
        )}
        {state.liveConfirmation && (
          <div className="flex w-1/2 gap-2 items-center justify-center rounded-md bg-neutral-200 dark:bg-neutral-800 p-2">
            <h5 className="font-bold">
              {!state.live ? 'Go Live?' : 'End Stream?'}
            </h5>
            <div className="flex gap-2 w-fit ml-2 items-center">
              <button
                onClick={() => handleGoLive(!state.live)}
                aria-label="Confirm"
                className="button-confirm rounded-full py-0.5 px-1"
              >
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleLiveConfirmationChange(false)}
                aria-label="Cancel"
                className="button-cancel rounded-full py-0.5 px-1"
              >
                <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        {state.loading && (
          <div className="text-green-500 my-auto">
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        )}
        {state.live && (
          <>
            {!state.resendConfirmation && (
              <button
                onClick={() => handleResendConfirmationChange(true)}
                aria-label="Resend Notification"
                className="button-fill-neutral w-1/2 text-base"
                disabled={state.loading}
              >
                {state.loading
                  ? 'Sending Notification...'
                  : 'Resend Notification'}
              </button>
            )}
            {state.resendConfirmation && (
              <div className="flex w-1/2 gap-2 items-center justify-center rounded-md bg-neutral-200 dark:bg-neutral-800 p-2">
                <h5 className="font-bold">Resend?</h5>
                <div className="flex gap-2 w-fit ml-2 items-center">
                  <button
                    onClick={() => handleGoLive(true)}
                    aria-label="Confirm"
                    className="button-confirm rounded-full py-0.5 px-1"
                  >
                    <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleResendConfirmationChange(false)}
                    aria-label="Cancel"
                    className="button-cancel rounded-full py-0.5 px-1"
                  >
                    <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            {state.loading && (
              <div className="text-green-500 my-auto">
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

SiteSettings.propTypes = {
  state: PropTypes.any,
  dispatch: PropTypes.any
}

export default SiteSettings
